import { Link } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles, ShoppingBag, LinkIcon, Youtube, BookOpen, Star, Check, Zap, TrendingUp, Quote, Mail, Award, Heart, FileText, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { EBOOKS, AI_TOOLS, AFFILIATE_PRODUCTS, SHORTS, BLOG_POSTS, FAQS, BRAND, TESTIMONIALS, TRUST_BADGES, TOP_PICKS } from "@/data/content";
import EbookModal from "@/components/EbookModal";
import { subscribeNewsletter } from "@/lib/api";
import { toast } from "sonner";

const HERO_IMG =
    "https://images.unsplash.com/photo-1487017159836-4e23ece2e4cf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwd29ya3NwYWNlJTIwbGFwdG9wJTIwZGVza3xlbnwwfHx8fDE3ODM2NTkxODd8MA&ixlib=rb-4.1.0&q=85";

const PORTRAIT =
    "https://images.pexels.com/photos/33568641/pexels-photo-33568641.jpeg";

function SectionTitle({ eyebrow, title, subtitle, to, ctaLabel = "See all" }) {
    return (
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10 md:mb-14">
            <div className="max-w-2xl">
                <p className="label-eyebrow text-primary">{eyebrow}</p>
                <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight mt-3 text-balance">
                    {title}
                </h2>
                {subtitle && <p className="mt-4 text-stone-600 text-lg leading-relaxed">{subtitle}</p>}
            </div>
            {to && (
                <Link
                    to={to}
                    className="inline-flex items-center gap-2 text-sm font-semibold link-underline text-primary"
                >
                    {ctaLabel} <ArrowUpRight className="h-4 w-4" />
                </Link>
            )}
        </div>
    );
}

export default function Home() {
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedEbook, setSelectedEbook] = useState(EBOOKS[0]);
    const [preFooterEmail, setPreFooterEmail] = useState("");
    const [preFooterLoading, setPreFooterLoading] = useState(false);

    const openEbook = (e) => {
        setSelectedEbook(e);
        setModalOpen(true);
    };

    const submitPreFooter = async (e) => {
        e.preventDefault();
        if (!preFooterEmail.trim()) return;
        setPreFooterLoading(true);
        try {
            await subscribeNewsletter(preFooterEmail.trim());
            toast.success("You're in. See you Monday.");
            setPreFooterEmail("");
        } catch (_) {
            toast.error("Couldn't subscribe. Please try again.");
        } finally {
            setPreFooterLoading(false);
        }
    };

    return (
        <>
            {/* HERO */}
            <section className="relative overflow-hidden" data-testid="hero-section">
                <div className="container-editorial pt-16 md:pt-24 pb-20 md:pb-28 grid md:grid-cols-12 gap-10 items-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="md:col-span-7"
                    >
                        <div className="inline-flex items-center gap-2 border border-stone-300 rounded-full px-3 py-1 mb-6">
                            <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse"></span>
                            <span className="text-xs tracking-wide text-stone-700">
                                Hand-tested picks — curated for real shoppers
                            </span>
                        </div>
                        <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl leading-[0.98] tracking-tight text-balance">
                            <span className="italic text-primary">Find it.</span> Love it. Buy it.
                            <span className="block text-3xl sm:text-4xl md:text-5xl mt-3 text-stone-700 not-italic font-medium">
                                Everything worth buying — curated for you.
                            </span>
                        </h1>
                        <p className="mt-5 text-lg md:text-xl text-stone-600 max-w-xl leading-relaxed">
                            Hand-tested product recommendations, <span className="font-semibold">honest reviews</span> and beginner-friendly <span className="font-semibold">buying guides</span> — no hype, no fake five-stars.
                        </p>

                        {/* Inline eBook mini-card — visible above the fold */}
                        <div
                            data-testid="hero-ebook-inline"
                            className="mt-6 flex items-center gap-4 bg-white border border-stone-200 rounded-xl p-4 max-w-lg shadow-[0_8px_32px_rgba(0,0,0,0.04)]"
                        >
                            <div className="w-14 h-14 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
                                <FileText className="h-6 w-6" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-[11px] tracking-widest uppercase font-bold text-accent">Free eBook · 33 pages</p>
                                <p className="font-serif text-lg leading-tight mt-0.5 truncate">25 Online Income Ideas for Beginners</p>
                            </div>
                            <Button
                                onClick={() => openEbook(EBOOKS[0])}
                                data-testid="hero-ebook-inline-cta"
                                className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shrink-0 h-10 px-4 text-sm"
                            >
                                Download
                            </Button>
                        </div>

                        <div className="mt-6 flex flex-wrap gap-3">
                            <Button
                                data-testid="hero-cta-primary"
                                onClick={() => openEbook(EBOOKS[0])}
                                className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-12 px-7 text-base"
                            >
                                Get the free eBook
                            </Button>
                            <Button
                                asChild
                                variant="outline"
                                data-testid="hero-cta-secondary"
                                className="rounded-full h-12 px-7 text-base border-stone-300 hover:bg-stone-100"
                            >
                                <Link to="/affiliate">See our picks <ArrowUpRight className="ml-1 h-4 w-4" /></Link>
                            </Button>
                        </div>

                        {/* Trust badges */}
                        <div data-testid="trust-badges" className="mt-8 flex flex-wrap gap-x-6 gap-y-3">
                            {TRUST_BADGES.map((b) => (
                                <div key={b.label} className="flex items-center gap-2 text-sm text-stone-700">
                                    <span className="w-5 h-5 rounded-full bg-primary/10 text-primary grid place-items-center">
                                        <Check className="h-3 w-3" strokeWidth={3} />
                                    </span>
                                    <span className="font-medium">{b.label}</span>
                                </div>
                            ))}
                        </div>

                        <dl className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
                            <div>
                                <dt className="text-xs tracking-widest uppercase text-stone-500">Products</dt>
                                <dd className="font-serif text-xl mt-1 leading-tight">Hand-tested</dd>
                            </div>
                            <div>
                                <dt className="text-xs tracking-widest uppercase text-stone-500">Reviews</dt>
                                <dd className="font-serif text-xl mt-1 leading-tight">100% honest</dd>
                            </div>
                            <div>
                                <dt className="text-xs tracking-widest uppercase text-stone-500">Guides</dt>
                                <dd className="font-serif text-xl mt-1 leading-tight">Free forever</dd>
                            </div>
                        </dl>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.9, delay: 0.2 }}
                        className="md:col-span-5 relative"
                    >
                        <div className="relative aspect-[4/5] rounded-lg overflow-hidden border border-stone-300">
                            <img src={HERO_IMG} alt="Modern workspace" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-t from-stone-950/40 via-transparent to-transparent" />
                            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-white">
                                <span className="label-eyebrow text-white/90">Featured</span>
                                <span className="text-xs">Issue №01</span>
                            </div>
                        </div>
                        <div className="absolute -bottom-6 -left-6 bg-white border border-stone-200 rounded-lg p-4 shadow-[0_8px_32px_rgba(0,0,0,0.06)] w-56 hidden md:block">
                            <div className="flex items-center gap-1 text-accent">
                                {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
                            </div>
                            <p className="text-sm mt-2 leading-snug text-stone-700">
                                "The 25 Ideas eBook is the first free guide I've read that didn't feel like a sales pitch."
                            </p>
                            <p className="text-xs text-stone-500 mt-2">— Priya M., new reader</p>
                        </div>
                    </motion.div>
                </div>

                {/* Marquee ribbon */}
                <div className="border-y border-stone-200 bg-stone-100 overflow-hidden">
                    <div className="flex whitespace-nowrap animate-marquee py-4 gap-14 font-serif italic text-stone-500 text-lg">
                        {[...Array(2)].flatMap((_, k) => [
                            "AI Tools", "Product Picks", "Buying Guides", "Honest Reviews", "Curated for You", "Free eBooks", "New Drops Weekly",
                        ].map((w, i) => (
                            <span key={`${k}-${i}`} className="flex items-center gap-14">
                                <span>{w}</span><span className="opacity-40">✦</span>
                            </span>
                        )))}
                    </div>
                </div>
            </section>

            {/* Today's Top Picks */}
            <section id="top-picks" data-testid="top-picks-section" className="container-editorial pt-20 md:pt-28">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10 md:mb-14">
                    <div className="max-w-2xl">
                        <p className="label-eyebrow text-accent flex items-center gap-2">
                            <Flame className="h-4 w-4" /> Today's Top Picks
                        </p>
                        <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight mt-3 text-balance">
                            The three we'd hand to a friend today.
                        </h2>
                        <p className="mt-4 text-stone-600 text-lg leading-relaxed">
                            One winner from each category — updated whenever we find something better.
                        </p>
                    </div>
                </div>
                <div className="grid md:grid-cols-3 gap-6">
                    {TOP_PICKS.map((p, i) => {
                        const Icon = p.icon === "sparkles" ? Sparkles : p.icon === "link" ? LinkIcon : p.icon === "book" ? BookOpen : ShoppingBag;
                        return (
                            <Link
                                key={p.slug}
                                to={p.to}
                                data-testid={`top-pick-${p.slug}`}
                                className="relative card-editorial rounded-lg p-7 flex flex-col group"
                            >
                                <span className="absolute -top-3 left-6 bg-accent text-accent-foreground text-[10px] tracking-widest uppercase font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1">
                                    <Flame className="h-3 w-3" /> #{i + 1}
                                </span>
                                <div className="flex items-start justify-between">
                                    <span className="w-11 h-11 rounded-lg bg-primary/10 text-primary grid place-items-center">
                                        <Icon className="h-5 w-5" />
                                    </span>
                                    <span className="text-[10px] tracking-widest uppercase font-semibold text-stone-500">
                                        {p.kind}
                                    </span>
                                </div>
                                <h3 className="font-serif text-2xl mt-5">{p.name}</h3>
                                <p className="text-xs uppercase tracking-widest text-stone-500 mt-1">{p.category}</p>
                                <p className="text-stone-600 mt-3 leading-relaxed flex-1">{p.blurb}</p>
                                <span className="mt-5 pt-4 border-t border-stone-200 text-primary font-semibold text-sm inline-flex items-center gap-1">
                                    {p.cta} <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 transition" />
                                </span>
                            </Link>
                        );
                    })}
                </div>
            </section>

            {/* eBook */}
            <section id="ebooks" data-testid="ebook-section" className="container-editorial py-20 md:py-28">
                <SectionTitle
                    eyebrow="Free eBook — Lead Magnet"
                    title="25 Proven Online Income Ideas for Beginners in 2026."
                    subtitle="A 33-page playbook with 25 real income streams — AI hustles, affiliate niches and digital products. Free forever."
                    to="/ebooks"
                    ctaLabel="See all eBooks"
                />

                <div className="grid md:grid-cols-12 gap-8 items-stretch">
                    <div className="md:col-span-5 relative aspect-[3/4] rounded-lg overflow-hidden border border-stone-200 bg-stone-100">
                        <img src={EBOOKS[0].cover} alt={EBOOKS[0].title} className="w-full h-full object-cover" />
                        <span className="absolute top-4 left-4 bg-accent text-accent-foreground text-[10px] tracking-widest uppercase font-bold px-2.5 py-1 rounded-full">
                            33 pages · Free
                        </span>
                    </div>

                    <div className="md:col-span-7 flex flex-col justify-center">
                        <h3 className="font-serif text-3xl md:text-4xl leading-tight">{EBOOKS[0].title}</h3>
                        <p className="text-stone-600 mt-3 text-lg">{EBOOKS[0].blurb}</p>
                        <ul className="mt-6 space-y-2.5">
                            {[
                                "25 online income ideas ranked by startup cost & effort",
                                "AI side hustles, digital products & freelancing — all inside",
                                "First-90-days action plan for every single idea",
                                "Beginner-friendly, zero jargon, zero fluff",
                            ].map((f) => (
                                <li key={f} className="flex items-start gap-3">
                                    <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                                    <span className="text-stone-700">{f}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Button
                                data-testid="ebook-primary-cta"
                                onClick={() => openEbook(EBOOKS[0])}
                                className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-12 px-7"
                            >
                                Send me the PDF
                            </Button>
                            <Link
                                to="/ebooks"
                                className="text-sm font-semibold link-underline self-center text-primary"
                            >
                                Or see the full library
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* AI Tools */}
            <section id="ai-tools" data-testid="ai-tools-section" className="bg-white border-y border-stone-200">
                <div className="container-editorial py-20 md:py-28">
                    <SectionTitle
                        eyebrow="AI Tools"
                        title="A curated stack — not a firehose of 800 tools."
                        subtitle="Only the AI tools we actually use, ranked by real-world usefulness."
                        to="/ai-tools"
                    />
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {AI_TOOLS.slice(0, 6).map((t) => (
                            <a
                                key={t.slug}
                                href={t.link}
                                data-testid={`ai-tool-card-${t.slug}`}
                                className="card-editorial rounded-lg p-6 flex flex-col group"
                            >
                                <div className="flex items-start justify-between">
                                    <span className="w-10 h-10 rounded-lg bg-primary/10 text-primary grid place-items-center">
                                        <Sparkles className="h-5 w-5" />
                                    </span>
                                    <span className="text-[10px] tracking-widest uppercase font-semibold text-stone-500">
                                        {t.category}
                                    </span>
                                </div>
                                <h3 className="font-serif text-2xl mt-5">{t.name}</h3>
                                <p className="text-stone-600 mt-2 text-sm leading-relaxed flex-1">{t.tagline}</p>
                                <div className="mt-5 pt-4 border-t border-stone-200 flex items-center justify-between text-sm">
                                    <span className="text-stone-500">{t.pricing}</span>
                                    <span className="flex items-center gap-1 text-accent font-semibold">
                                        <Star className="h-4 w-4 fill-current" /> {t.rating}
                                    </span>
                                </div>
                            </a>
                        ))}
                    </div>
                </div>
            </section>

            {/* Digital Shop — SuperProfile */}
            <section id="shop" data-testid="shop-section" className="container-editorial py-20 md:py-28">
                <div className="grid md:grid-cols-12 gap-10 items-center bg-stone-100 rounded-2xl p-10 md:p-16 border border-stone-200 relative overflow-hidden">
                    <div className="md:col-span-7">
                        <p className="label-eyebrow text-accent flex items-center gap-2">
                            <ShoppingBag className="h-4 w-4" /> Digital Shop
                        </p>
                        <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight mt-3 text-balance">
                            Grab our premium digital guides & templates.
                        </h2>
                        <p className="mt-4 text-stone-600 text-lg leading-relaxed max-w-lg">
                            Deep-dive playbooks, prompt packs and templates — hosted on our SuperProfile shop. Instant download, UPI + card accepted, lifetime access.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Button
                                asChild
                                data-testid="shop-cta-primary"
                                className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-12 px-7 text-base"
                            >
                                <a href="https://superprofile.bio/launchtoearn" target="_blank" rel="noopener noreferrer">
                                    <ShoppingBag className="h-4 w-4 mr-2" /> Visit the shop
                                </a>
                            </Button>
                            <Button
                                asChild
                                variant="outline"
                                data-testid="shop-cta-secondary"
                                className="rounded-full h-12 px-7 border-stone-300 hover:bg-white"
                            >
                                <Link to="/ebooks">Or grab a free eBook <ArrowUpRight className="ml-1 h-4 w-4" /></Link>
                            </Button>
                        </div>
                    </div>
                    <div className="md:col-span-5">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="aspect-square rounded-xl bg-white border border-stone-200 grid place-items-center p-6">
                                <div className="text-center">
                                    <BookOpen className="h-8 w-8 text-primary mx-auto" />
                                    <p className="font-serif text-lg mt-3 leading-tight">Playbooks</p>
                                </div>
                            </div>
                            <div className="aspect-square rounded-xl bg-white border border-stone-200 grid place-items-center p-6 mt-8">
                                <div className="text-center">
                                    <Sparkles className="h-8 w-8 text-primary mx-auto" />
                                    <p className="font-serif text-lg mt-3 leading-tight">Prompt Packs</p>
                                </div>
                            </div>
                            <div className="aspect-square rounded-xl bg-white border border-stone-200 grid place-items-center p-6 -mt-4">
                                <div className="text-center">
                                    <FileText className="h-8 w-8 text-primary mx-auto" />
                                    <p className="font-serif text-lg mt-3 leading-tight">Templates</p>
                                </div>
                            </div>
                            <div className="aspect-square rounded-xl bg-white border border-stone-200 grid place-items-center p-6 mt-4">
                                <div className="text-center">
                                    <Zap className="h-8 w-8 text-primary mx-auto" />
                                    <p className="font-serif text-lg mt-3 leading-tight">Guides</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Dropshipping section removed */}

            {/* Affiliate */}
            <section id="affiliate" data-testid="affiliate-section" className="bg-primary text-primary-foreground grain relative">
                <div className="container-editorial py-20 md:py-28">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10 md:mb-14">
                        <div className="max-w-2xl">
                            <p className="label-eyebrow text-accent">Our Picks</p>
                            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight mt-3 text-balance">
                                Hand-tested products, curated for you.
                            </h2>
                            <p className="mt-4 text-stone-200 text-lg leading-relaxed">
                                Every recommendation is something we'd buy again. Some links are partner links — we may earn a small referral reward at no extra cost to you.
                            </p>
                        </div>
                        <Link to="/affiliate" className="inline-flex items-center gap-2 text-sm font-semibold link-underline text-accent">
                            See all picks <ArrowUpRight className="h-4 w-4" />
                        </Link>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {AFFILIATE_PRODUCTS.slice(0, 6).map((p) => (
                            <a
                                key={p.slug}
                                href={p.link}
                                data-testid={`aff-card-${p.slug}`}
                                className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg p-6 transition-all hover:-translate-y-1"
                            >
                                <div className="flex items-center justify-between">
                                    <span className="w-10 h-10 rounded-lg bg-accent/20 text-accent grid place-items-center">
                                        <LinkIcon className="h-5 w-5" />
                                    </span>
                                    {p.tag && <Badge className="bg-accent text-accent-foreground hover:bg-accent border-0 text-[10px]">{p.tag}</Badge>}
                                </div>
                                <h3 className="font-serif text-2xl mt-5">{p.name}</h3>
                                <p className="text-stone-300 text-sm mt-2">{p.blurb}</p>
                                <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between text-sm">
                                    <span className="text-stone-400">{p.category}</span>
                                    <span className="text-accent">{p.price}</span>
                                </div>
                            </a>
                        ))}
                    </div>
                </div>
            </section>

            {/* Shorts */}
            <section id="shorts" data-testid="shorts-section" className="container-editorial py-20 md:py-28">
                <SectionTitle
                    eyebrow="YouTube Shorts"
                    title="Quick playbooks. Vertical. Snackable."
                    subtitle="60-second product reviews, buying guides and quick tips — all snackable."
                    to="/shorts"
                    ctaLabel="Watch more"
                />
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {SHORTS.slice(0, 6).map((s) => (
                        <div key={s.id} data-testid={`short-${s.id}`} className="card-editorial rounded-lg overflow-hidden flex flex-col">
                            <div className="aspect-[9/16] bg-stone-900 relative">
                                <iframe
                                    className="absolute inset-0 w-full h-full"
                                    src={`https://www.youtube.com/embed/${s.id}`}
                                    title={s.title}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    loading="lazy"
                                />
                            </div>
                            <div className="p-3">
                                <p className="text-xs leading-snug font-medium line-clamp-2">{s.title}</p>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
                    <Button asChild data-testid="shorts-watch-more" className="rounded-full bg-primary hover:bg-primary/90 h-12 px-7 text-primary-foreground">
                        <Link to="/shorts">Watch more Shorts <ArrowUpRight className="ml-1 h-4 w-4" /></Link>
                    </Button>
                    <Button asChild variant="outline" data-testid="home-shorts-subscribe" className="rounded-full h-12 px-7 border-stone-300">
                        <a href={BRAND.socials.youtube} target="_blank" rel="noopener noreferrer">
                            <Youtube className="h-4 w-4 mr-2" /> Subscribe on {BRAND.youtubeHandle}
                        </a>
                    </Button>
                </div>
            </section>

            {/* Testimonials */}
            <section id="testimonials" data-testid="testimonials-section" className="bg-white border-y border-stone-200">
                <div className="container-editorial py-20 md:py-28">
                    <div className="max-w-2xl mb-12">
                        <p className="label-eyebrow text-primary">Reader Feedback</p>
                        <h2 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight mt-3">
                            Early readers, real reactions.
                        </h2>
                        <p className="mt-4 text-stone-600 text-lg">
                            A handful of first responses from the early AllAboutYouBuy community.
                        </p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                        {TESTIMONIALS.map((t, i) => (
                            <figure key={i} data-testid={`testimonial-${i}`} className="card-editorial rounded-lg p-6 flex flex-col">
                                <Quote className="h-8 w-8 text-accent/70" />
                                <blockquote className="font-serif text-lg leading-snug mt-4 flex-1">
                                    "{t.quote}"
                                </blockquote>
                                <div className="mt-5 pt-4 border-t border-stone-200 flex items-center justify-between">
                                    <div>
                                        <p className="font-semibold text-sm">{t.name}</p>
                                        <p className="text-xs text-stone-500">{t.role}</p>
                                    </div>
                                    <div className="flex items-center gap-0.5 text-accent">
                                        {Array.from({ length: t.rating }).map((_, k) => (
                                            <Star key={k} className="h-3.5 w-3.5 fill-current" />
                                        ))}
                                    </div>
                                </div>
                            </figure>
                        ))}
                    </div>
                </div>
            </section>

            {/* Blog */}
            <section id="blog" data-testid="blog-section" className="bg-white border-y border-stone-200">
                <div className="container-editorial py-20 md:py-28">
                    <SectionTitle
                        eyebrow="Blog"
                        title="Buying guides & honest deep-dives."
                        to="/blog"
                    />
                    <div className="grid md:grid-cols-12 gap-8">
                        {BLOG_POSTS.slice(0, 1).map((p) => (
                            <Link
                                key={p.slug}
                                to={`/blog/${p.slug}`}
                                data-testid={`blog-featured-${p.slug}`}
                                className="md:col-span-7 group"
                            >
                                <div className="aspect-[4/3] overflow-hidden rounded-lg border border-stone-200">
                                    <img src={p.cover} alt={p.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition duration-500" />
                                </div>
                                <div className="mt-5">
                                    <span className="label-eyebrow text-accent">{p.category} · {p.readingTime} min read</span>
                                    <h3 className="font-serif text-3xl md:text-4xl mt-2 leading-tight group-hover:text-primary transition">{p.title}</h3>
                                    <p className="text-stone-600 mt-3">{p.excerpt}</p>
                                </div>
                            </Link>
                        ))}
                        <div className="md:col-span-5 flex flex-col divide-y divide-stone-200">
                            {BLOG_POSTS.slice(1, 4).map((p) => (
                                <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`blog-item-${p.slug}`} className="py-5 group first:pt-0 last:pb-0">
                                    <span className="label-eyebrow text-accent">{p.category}</span>
                                    <h4 className="font-serif text-xl mt-1 group-hover:text-primary">{p.title}</h4>
                                    <p className="text-sm text-stone-600 mt-2 line-clamp-2">{p.excerpt}</p>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* About */}
            <section id="about" data-testid="about-section" className="container-editorial py-20 md:py-28">
                <div className="grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-5">
                        <div className="aspect-[3/4] rounded-lg overflow-hidden border border-stone-200">
                            <img src={PORTRAIT} alt="Founder" className="w-full h-full object-cover" />
                        </div>
                    </div>
                    <div className="md:col-span-7">
                        <p className="label-eyebrow text-primary">About</p>
                        <h2 className="font-serif text-4xl md:text-5xl leading-tight mt-3">
                            Welcome to <span className="italic">AllAboutYouBuy</span>.
                        </h2>
                        <p className="text-stone-700 text-lg mt-5 leading-relaxed">
                            Our mission is to help you buy smarter — hand-tested product picks, honest reviews and
                            beginner-friendly buying guides for every budget.
                        </p>
                        <p className="text-stone-600 mt-4 leading-relaxed">
                            We publish honest reviews, practical guides and free resources — so you can find it, love it and buy it with confidence.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Button asChild data-testid="about-cta" className="rounded-full bg-primary hover:bg-primary/90 h-12 px-7 text-primary-foreground">
                                <Link to="/about">Read the full story</Link>
                            </Button>
                            <Button asChild variant="outline" className="rounded-full h-12 px-7 border-stone-300">
                                <Link to="/contact">Say hello</Link>
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQs */}
            <section id="faqs" data-testid="faq-section" className="bg-stone-100 border-y border-stone-200">
                <div className="container-editorial py-20 md:py-28 grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-5">
                        <p className="label-eyebrow text-primary">FAQs</p>
                        <h2 className="font-serif text-4xl md:text-5xl leading-tight mt-3">
                            Answers, straight up.
                        </h2>
                        <p className="text-stone-600 mt-4">
                            Still curious?{" "}
                            <Link to="/contact" className="underline text-primary">Send us a note</Link>.
                        </p>
                    </div>
                    <div className="md:col-span-7">
                        <Accordion type="single" collapsible className="w-full" data-testid="home-faq-accordion">
                            {FAQS.map((f, i) => (
                                <AccordionItem key={i} value={`item-${i}`} className="border-stone-300">
                                    <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-serif text-lg hover:no-underline">
                                        {f.q}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-stone-700 leading-relaxed">{f.a}</AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </div>
            </section>

            {/* Pre-footer newsletter capture */}
            <section id="join" data-testid="prefooter-newsletter" className="container-editorial py-20 md:py-28">
                <div className="bg-stone-100 border border-stone-200 rounded-2xl px-8 md:px-16 py-14 md:py-20 grid md:grid-cols-12 gap-8 items-center">
                    <div className="md:col-span-7">
                        <p className="label-eyebrow text-accent">Newsletter</p>
                        <h2 className="font-serif text-3xl md:text-5xl leading-tight mt-3">
                            Get one practical online business tip every week.
                        </h2>
                        <p className="mt-4 text-stone-600 text-lg">
                            Join a growing community of shoppers getting one curated product pick, deal alert or buying tip — one email, every Monday.
                        </p>
                    </div>
                    <form onSubmit={submitPreFooter} className="md:col-span-5 flex flex-col gap-3">
                        <label className="text-xs uppercase tracking-widest text-stone-500">Your best email</label>
                        <Input
                            data-testid="prefooter-newsletter-input"
                            type="email"
                            required
                            value={preFooterEmail}
                            onChange={(e) => setPreFooterEmail(e.target.value)}
                            placeholder="you@example.com"
                            className="h-12 bg-white"
                        />
                        <Button
                            data-testid="prefooter-newsletter-submit"
                            type="submit"
                            disabled={preFooterLoading}
                            className="h-12 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground"
                        >
                            <Mail className="h-4 w-4 mr-2" />
                            {preFooterLoading ? "Adding you…" : "Get the weekly email"}
                        </Button>
                        <p className="text-[11px] text-stone-500">No spam. Unsubscribe any time.</p>
                    </form>
                </div>
            </section>

            {/* Contact CTA */}
            <section id="contact" data-testid="contact-cta-section" className="container-editorial py-20 md:py-28">
                <div className="bg-primary text-primary-foreground rounded-2xl p-10 md:p-16 grain relative overflow-hidden">
                    <div className="max-w-2xl">
                        <p className="label-eyebrow text-accent">Let's talk</p>
                        <h2 className="font-serif text-4xl md:text-5xl leading-tight mt-3">
                            Have a question, tip or collab idea?
                        </h2>
                        <p className="mt-4 text-stone-200 text-lg">
                            We read everything and reply within 48 hours.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Button asChild data-testid="contact-cta-btn" className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground h-12 px-7">
                                <Link to="/contact">Contact us</Link>
                            </Button>
                            <Button asChild variant="outline" className="rounded-full h-12 px-7 bg-transparent text-primary-foreground border-primary-foreground/30 hover:bg-white/10">
                                <Link to="/faqs">Read FAQs</Link>
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            <EbookModal open={modalOpen} onOpenChange={setModalOpen} ebook={selectedEbook} />
        </>
    );
}
